/**
 * Login Page
 * User authentication page - Bulgarian language
 */

import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { AuthLayout, AuthBrand } from './AuthLayout';
import { forgotPassword } from '@/services/auth.service';

export const LoginPage: React.FC = () => {
  const { login, isLoading, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [formErrors, setFormErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  // Forgot-password modal state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError(null);

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      setForgotError('Невалиден имейл адрес');
      return;
    }

    setForgotSubmitting(true);
    try {
      await forgotPassword(forgotEmail);
      setForgotDone(true);
    } catch {
      // Backend always returns a generic success; treat errors generically too
      setForgotDone(true);
    } finally {
      setForgotSubmitting(false);
    }
  };

  const closeForgot = () => {
    setShowForgot(false);
    setForgotEmail('');
    setForgotDone(false);
    setForgotError(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field error when user starts typing
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Clear global error
    if (error) {
      clearError();
    }
  };

  const validateForm = (): boolean => {
    const errors: typeof formErrors = {};

    if (!formData.email.trim()) {
      errors.email = 'Полето е задължително';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Невалиден имейл адрес';
    }

    if (!formData.password) {
      errors.password = 'Полето е задължително';
    } else if (formData.password.length < 8) {
      errors.password = 'Паролата трябва да е поне 8 символа';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await login({
      email: formData.email,
      password: formData.password,
    });
  };

  return (
    <AuthLayout>
      {/* Brand */}
      <AuthBrand />

      {/* Error Alert */}
      {error && (
        <div className="auth-error px-4 py-3 rounded-xl">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <div className="space-y-4">
          <Input
            label="Имейл адрес"
            type="email"
            name="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            error={formErrors.email}
            placeholder="user@super5.space"
            required
          />

          <Input
            label="Парола"
            type="password"
            name="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            error={formErrors.password}
            placeholder="--------"
            required
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 accent-primary-500 rounded border-white/10 bg-white/5"
            />
            <label
              htmlFor="remember-me"
              className="ml-2 block text-sm text-gray-400"
            >
              Запомни ме
            </label>
          </div>

          <div className="text-sm">
            <button
              type="button"
              className="font-medium text-primary-400 hover:text-primary-300 transition-colors"
              onClick={() => {
                setForgotEmail(formData.email);
                setShowForgot(true);
              }}
            >
              Забравена парола?
            </button>
          </div>
        </div>

        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          fullWidth
          className="py-3 rounded-xl font-semibold"
        >
          Влезте
        </Button>
      </form>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={closeForgot}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-dark-bg-secondary border border-dark-border-primary p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Възстановяване на парола"
          >
            {forgotDone ? (
              <div className="space-y-4 text-center">
                <h3 className="text-lg font-semibold text-dark-text-primary">Проверете имейла си</h3>
                <p className="text-sm text-dark-text-secondary">
                  Ако съществува акаунт с този имейл, ще получите инструкции за възстановяване на паролата.
                </p>
                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  className="py-2.5 rounded-xl font-semibold"
                  onClick={closeForgot}
                >
                  Затвори
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-dark-text-primary">Забравена парола</h3>
                  <p className="mt-1 text-sm text-dark-text-secondary">
                    Въведете имейла си и ще ви изпратим връзка за възстановяване.
                  </p>
                </div>

                {forgotError && (
                  <div className="auth-error px-3 py-2 rounded-lg">
                    <p className="text-sm">{forgotError}</p>
                  </div>
                )}

                <Input
                  label="Имейл адрес"
                  type="email"
                  name="forgot-email"
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="user@super5.space"
                  required
                />

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    fullWidth
                    className="py-2.5 rounded-xl font-semibold"
                    onClick={closeForgot}
                  >
                    Отказ
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={forgotSubmitting}
                    fullWidth
                    className="py-2.5 rounded-xl font-semibold"
                  >
                    Изпрати
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

    </AuthLayout>
  );
};
